import { Suspense, lazy } from "react";
import { HashRouter as Router, Routes as ReactRouterRoutes, Route, Outlet } from "react-router-dom";
import { Backdrop, CircularProgress, Grid, Link } from "@mui/material";
import { LoginWithMicrosoft, NotFound, LoginWithToken } from "@sal/portal";
import BasePage from "./pages/BasePage";
import { LoginPage } from "./pages/LoginPage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { LogoutPage } from "./pages/LogoutPage";
import { routePermissionMapping } from "./Settings";
import {
	PortalAuthenticatedRoute as _PortalAuthenticatedRoute,
	PortalAuthenticatedRouteProps,
	usePortalApplicationContext
} from "@sal/onevent-portal";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { useAuth } from "@sal/authentication.react";

const HomePage = lazy(() => import("./pages/HomePage"));
const ShipmentDashboardPage = lazy(() => import("./pages/ShipmentDashboardPage"));
const ShipmentTrackerPage = lazy(() => import("./pages/ShipmentTrackerPage"));
const TransportPage = lazy(() => import("./pages/TransportPage"));
const TransportDetailsPage = lazy(() => import("./pages/TransportDetailsPage"));
const AccountsPage = lazy(() => import("./pages/AccountsPage"));
const ImportOrderPage = lazy(() => import("./pages/ImportOrderPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const EstimatePage = lazy(() => import("./pages/EstimatePage"));
const MyProfile = lazy(() => import("./pages/myProfile/MyProfile"));
const UserDashboardPage = lazy(() => import("./pages/UserDashboardPage"));
const UserDetailPage = lazy(() => import("./pages/UserDetailPage"));
const UserNewPage = lazy(() => import("./pages/UserNewPage"));
const SchedulePage = lazy(() => import("./pages/SchedulesPage"));
const ContainerPage = lazy(() => import("./pages/ContainerTrackerPage"));
const OrderTrackerPage = lazy(() => import("./pages/OrderTrackerPage"));
const DeliveryTrackingPage = lazy(() => import("./pages/DeliveryTrackingPage"));
const MyAccountPage = lazy(() => import("./pages/MyAccountPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ManageStaffContactsPage = lazy(() => import("./pages/ManageStaffContactsPage"));
const TEUCalendarPage = lazy(() => import("./pages/TEUCalendarPage"));
const StaffContactsPage = lazy(() => import("./pages/StaffContactsPage"));
const ExportBookingPage = lazy(() => import("./pages/ExportBookingPage"));
const DocumentPermalinkPage = lazy(() => import("./pages/DocumentPermalinkPage"));
const PortCallsPage = lazy(() => import("./pages/AdminPortCalls"));
const CustomersDashboardPage = lazy(() => import("./pages/CustomersDashboardPage"));
const CustomerPage = lazy(() => import("./pages/CustomerPage"));
const BulkUpdatePage = lazy(() => import("./pages/BulkUpdatePage"));

// TODO: Remove after tested
const MUITestReportPage =  lazy(() => import("./pages/ReportMUITestPage"));

const {
	track,
	order,
	estimate,
	accounts,
	transport,
	analytics,
	reports,
	schedule,
	users,
	admin,
	manageStaffContacts,
	arrivalsCalendar,
	staffContacts,
	exportBooking,
	portCalls,
	customersDashboard,
	customer,
	bulkUpdate
} = routePermissionMapping;

function PortalAuthenticatedRoute(props: PortalAuthenticatedRouteProps) {
	const auth = useAuth();
	const loginPath = auth.authenticationMethod === "SalCore" ? "/LoginSalCore" : "/Login";
	return <_PortalAuthenticatedRoute {...props} loginPath={loginPath} />;
}

export default function Routes() {
	const { organisations, impersonation } = usePortalApplicationContext();

	const { renderToast } = impersonation;

	return (
		<Router>
			<ReactRouterRoutes>
				<Route element={<PortalAuthenticatedRoute />}>
					<Route
						element={
							<>
								{renderToast}
								<BasePage />
							</>
						}
					>
						<Route
							element={
								<Suspense
									fallback={
										<Backdrop invisible open>
											<CircularProgress />
										</Backdrop>
									}
								>
									<Outlet />
								</Suspense>
							}
						>
							<Route path="/" element={<HomePage />} />
							<Route
								element={
									<PortalAuthenticatedRoute
										securityContext={track.securityContext}
										securityContextPermission={track.securityContextPermission}
									/>
								}
							>
								<Route path={track.path} element={<ShipmentDashboardPage />} />
								<Route path="/Shipments" element={<ShipmentTrackerPage />} />
								<Route path="/Containers" element={<ContainerPage />} />
								<Route path="/Orders" element={<OrderTrackerPage />} />
								<Route path="/DeliveryTracking" element={<DeliveryTrackingPage />} />
							</Route>
							<Route
								path={arrivalsCalendar.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={arrivalsCalendar.securityContext}
										securityContextPermission={arrivalsCalendar.securityContextPermission}
									>
										<TEUCalendarPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={order.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={order.securityContext}
										securityContextPermission={order.securityContextPermission}
									>
										<ImportOrderPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={exportBooking.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={exportBooking.securityContext}
										securityContextPermission={exportBooking.securityContextPermission}
									>
										<ExportBookingPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={estimate.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={estimate.securityContext}
										securityContextPermission={estimate.securityContextPermission}
									>
										<EstimatePage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={accounts.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={accounts.securityContext}
										securityContextPermission={accounts.securityContextPermission}
									>
										<AccountsPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={transport.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={transport.securityContext}
										securityContextPermission={transport.securityContextPermission}
									/>
								}
							>
								<Route index element={<TransportPage />} />
								<Route path=":orgId/:partyId/:orgCode/:partyRef" element={<TransportDetailsPage />} />
							</Route>
							<Route
								path={analytics.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={analytics.securityContext}
										securityContextPermission={analytics.securityContextPermission}
									>
										<AnalyticsPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={reports.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={reports.securityContext}
										securityContextPermission={reports.securityContextPermission}
									>
										<ReportsPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={schedule.path}
								element={
									<PortalAuthenticatedRoute permissions={schedule.permissions || []}>
										<SchedulePage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={admin.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={admin.securityContext}
										securityContextPermission={admin.securityContextPermission}
									>
										<AdminPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={manageStaffContacts.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={manageStaffContacts.securityContext}
										securityContextPermission={manageStaffContacts.securityContextPermission}
									>
										<ManageStaffContactsPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={staffContacts.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={staffContacts.securityContext}
										securityContextPermission={staffContacts.securityContextPermission}
									>
										<StaffContactsPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path={users.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={users.securityContext}
										securityContextPermission={users.securityContextPermission}
									/>
								}
							>
								<Route index element={<UserDashboardPage />} />
								<Route path=":id" element={<UserDetailPage />} />
								<Route
									path="new"
									element={
										<PortalAuthenticatedRoute
											securityContext="apI_User"
											securityContextPermission="Write"
										>
											<UserNewPage />
										</PortalAuthenticatedRoute>
									}
								/>
							</Route>
							<Route
								path={portCalls.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={portCalls.securityContext}
										securityContextPermission={portCalls.securityContextPermission}
									>
										<PortCallsPage />
									</PortalAuthenticatedRoute>
								}
							/>
							<Route
								path="/Customers"
								element={
									<PortalAuthenticatedRoute
										securityContext={customersDashboard.securityContext}
										securityContextPermission={customersDashboard.securityContextPermission}
									/>
								}
							>
								<Route index element={<CustomersDashboardPage />} />
							</Route>

							<Route
								path="/Customer/:id"
								element={
									<PortalAuthenticatedRoute
										securityContext={customer.securityContext}
										securityContextPermission={customer.securityContextPermission}
									/>
								}
							>
								<Route index element={<CustomerPage />} />
							</Route>
							<Route
								path={bulkUpdate.path}
								element={
									<PortalAuthenticatedRoute
										securityContext={bulkUpdate.securityContext}
										securityContextPermission={bulkUpdate.securityContextPermission}
									/>
								}
							>
								<Route index element={<BulkUpdatePage />} />
							</Route>

							<Route path="/MyProfile" element={<MyProfile />} />
							<Route path="/MyAccount" element={<MyAccountPage />} />
							<Route path="/Document" element={<DocumentPermalinkPage />} />

							<Route
								path="/NewReportTest"
								element={
									<PortalAuthenticatedRoute securityContext={"additionAdmin"} securityContextPermission={"UnrestrictReport"}>
										<MUITestReportPage />
									</PortalAuthenticatedRoute>
								}
							/>
						</Route>
					</Route>
				</Route>
				<Route path="/Logout" element={<LogoutPage />} />
				{/* PORTAL FRAMEWORK PAGES*/}
				<Route path="/LoginWithToken/:token/:returnUrl?" element={<LoginWithToken />} />
				<Route
					path="/ChangePassword"
					element={
						<PortalAuthenticatedRoute
							securityContext="web_Host"
							securityContextPermission="Change_Password"
						>
							<ChangePasswordPage />
						</PortalAuthenticatedRoute>
					}
				/>
				<Route path="/LoginSalCore" element={<LoginPage />} />
				<Route
					path="/Login"
					element={
						<LoginWithMicrosoft
							onLogin={() => {
								organisations.clearOrganisations();
							}}
						/>
					}
				/>
				<Route path="/ForgotPassword" element={<ForgotPasswordPage />} />
				<Route path="/ResetPassword/:passwordResetToken" element={<ResetPasswordPage />} />
				<Route
					path="/NotFound"
					element={
						<>
							{renderToast}
							<NotFound
								returnLinkComponent={
									<Grid item>
										<Link href="./#/Track">Go to track dashboard</Link>
									</Grid>
								}
							/>
						</>
					}
				/>
				<Route
					path="*"
					element={
						<>
							{renderToast}
							<NotFound
								returnLinkComponent={
									<Grid item>
										<Link href="./#/Track">Go to track dashboard</Link>
									</Grid>
								}
							/>
						</>
					}
				/>
				{/* /PORTAL FRAMEWORK PAGES*/}
			</ReactRouterRoutes>
		</Router>
	);
}
